import type { ScoreSchema, SchemaParam } from './types'

// Mirrors ScoreSchemaParser.cs param resolution: walk the raw JSON tree replacing
// "$param:key" strings with the resolved value (provided values > declared defaults).
// Full structural validation is server-side; the client only needs a parsable,
// resolved schema.

const PARAM_REF_PREFIX = '$param:'

export class ScoreSchemaError extends Error {}

export function parseScoreSchema(schemaJson: string, paramValuesJson?: string | null): ScoreSchema {
    let root: unknown
    try {
        root = JSON.parse(schemaJson)
    } catch (e) {
        throw new ScoreSchemaError(`Schema is not valid JSON: ${(e as Error).message}`)
    }

    if (typeof root !== 'object' || root === null) throw new ScoreSchemaError('Schema is empty')

    const declared = ((root as Record<string, unknown>).params ?? []) as SchemaParam[]
    const values = resolveParamValues(declared, paramValuesJson)
    const resolved = resolveParamRefs(root, values) as Record<string, unknown>

    return {
        version: (resolved.version as number) ?? 1,
        kind: (resolved.kind as string) ?? 'score-schema',
        sides: (resolved.sides as string[]) ?? ['home', 'away'],
        params: declared,
        units: (resolved.units as ScoreSchema['units']) ?? {},
        rootUnit: (resolved.rootUnit as string) ?? 'match',
        turns: (resolved.turns as ScoreSchema['turns']) ?? null,
        counters: (resolved.counters as ScoreSchema['counters']) ?? {},
        displayMaps: (resolved.displayMaps as ScoreSchema['displayMaps']) ?? {},
        events: (resolved.events as ScoreSchema['events']) ?? [],
        resultMapping: (resolved.resultMapping as ScoreSchema['resultMapping']) ?? { type: 'childUnitsWon', map: [] },
        entryModes: (resolved.entryModes as string[]) ?? ['resultOnly'],
    }
}

function resolveParamValues(declared: SchemaParam[], paramValuesJson?: string | null): Map<string, unknown> {
    let provided: Record<string, unknown> = {}
    if (paramValuesJson) {
        try {
            provided = JSON.parse(paramValuesJson)
        } catch (e) {
            throw new ScoreSchemaError(`SchemaParamValues is not valid JSON: ${(e as Error).message}`)
        }
    }

    const values = new Map<string, unknown>()
    for (const param of declared) {
        const value = provided[param.key]
        if (value !== undefined && value !== null) {
            if (param.type === 'int' && typeof value === 'number') {
                if (param.min != null && value < param.min)
                    throw new ScoreSchemaError(`param '${param.key}' value ${value} is below min ${param.min}`)
                if (param.max != null && value > param.max)
                    throw new ScoreSchemaError(`param '${param.key}' value ${value} is above max ${param.max}`)
            }
            values.set(param.key, value)
        } else if (param.default !== undefined && param.default !== null) {
            values.set(param.key, param.default)
        } else if (!param.optional) {
            throw new ScoreSchemaError(`param '${param.key}' has no value and no default`)
        } else {
            values.set(param.key, null)
        }
    }

    return values
}

function resolveParamRefs(node: unknown, values: Map<string, unknown>): unknown {
    if (typeof node === 'string' && node.startsWith(PARAM_REF_PREFIX)) {
        const key = node.slice(PARAM_REF_PREFIX.length)
        if (!values.has(key)) throw new ScoreSchemaError(`schema references undeclared param '${key}'`)
        return values.get(key)
    }

    if (Array.isArray(node)) return node.map((child) => resolveParamRefs(child, values))

    if (typeof node === 'object' && node !== null) {
        const result: Record<string, unknown> = {}
        for (const [key, child] of Object.entries(node)) {
            result[key] = resolveParamRefs(child, values)
        }
        return result
    }

    return node
}
